import { CurrencyPipe, isPlatformServer } from '@angular/common';
import { HttpClient, provideHttpClient, withFetch } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  isDevMode,
  PLATFORM_ID,
  provideZonelessChangeDetection,
} from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { ApiConfiguration } from '@klubovna/api-client';
import { APP_CONFIG, documentsReducer, DocumentsEffects } from '@klubovna/shared-data';
import { environment } from '@environment';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { TranslateLoader, TranslateModule, TranslationObject } from '@ngx-translate/core';
import { provideToastr } from 'ngx-toastr';
import * as Sentry from '@sentry/angular';
import {
  CUSTOM_DATE_FORMATS,
  CustomDateAdapter,
  DEFAULT_LANGUAGE,
  GlobalErrorHandlerService,
} from '@shared';

import { routes } from './app.routes';
import { albumsReducer } from './store/albums/albums.reducer';
import { AlbumsEffects } from './store/albums/albums.effects';
import { eventsReducer } from './store/events/events.reducer';
import { EventsEffects } from './store/events/events.effects';
import { volunteersReducer } from './store/volunteers/volunteers.reducer';
import { VolunteersEffects } from './store/volunteers/volunteers.effects';

export function translateLoaderFactory(http: HttpClient, platformId: object): TranslateLoader {
  const serverPort =
    typeof globalThis !== 'undefined' && (globalThis as any)?.process?.env?.PORT
      ? (globalThis as any).process.env.PORT
      : '4000';
  const serverPrefix = `http://127.0.0.1:${ serverPort }/assets/i18n/`;
  const browserPrefix = './assets/i18n/';
  const prefix = isPlatformServer(platformId) ? serverPrefix : browserPrefix;
  return {
    getTranslation: (lang: string) => http.get<TranslationObject>(`${ prefix }${ lang }.json`),
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),
    provideAnimations(),
    provideClientHydration(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerImmediately',
    }),
    provideStore({
      events: eventsReducer,
      documents: documentsReducer,
      volunteers: volunteersReducer,
      presentationAlbums: albumsReducer,
    }),
    provideEffects([ EventsEffects, DocumentsEffects, VolunteersEffects, AlbumsEffects ]),
    provideHttpClient(withFetch()),
    {
      provide: ApiConfiguration,
      useFactory: () => {
        const config = new ApiConfiguration();
        config.rootUrl = environment.apiUrl;
        return config;
      },
    },
    provideToastr({
      timeOut: 3000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
      progressBar: true,
      closeButton: true,
      enableHtml: true,
    }),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: translateLoaderFactory,
          deps: [ HttpClient, PLATFORM_ID ],
        },
        fallbackLang: DEFAULT_LANGUAGE,
      }),
      MatDialogModule,
    ),
    CurrencyPipe,
    { provide: APP_CONFIG, useValue: environment },
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    { provide: MAT_DATE_LOCALE, useValue: 'sk-SK' },
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    ...(environment.sentryLogging ? [ Sentry.TraceService ] : []),
  ],
};
